import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactPaginate from 'react-paginate';
import { useRouter } from 'next/router';

import { StateContext, DispatchContext } from '@/context/index';

import { getTagIdsBySlug, useDynamicPages } from '@/utils/api';
import { getPageNumber } from '@/utils/helper';
import { analytics, getPaginationDetail } from '@/utils/analytics';

import Text from '@/atoms/Text/Text';
import ContentGrid from '@/organisms/ContentGrid/ContentGrid';

import {
  ContentQueryOrder,
  CtfContentQueryOrder,
  PaginationDefaults,
} from '@/utils/constants';

function DynamicGrid({
  assets,
  entries,
  fields,
  columnLayout,
  isFromTwoColumnContent,
  isFromContentColumn,
  hasBackground,
}) {
  const router = useRouter();
  const { pages, queryTagIds, queryTagsKey, totalPages } = fields;
  const [newAssets, setNewAssets] = useState(assets);
  const [newEntries, setNewEntries] = useState(entries);
  const {
    dynamicGridParam: { [queryTagsKey]: ctxDynamicGridParam },
  } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const currentPage = getPageNumber(router.asPath) || 1;

  useEffect(() => {
    if (currentPage !== 1) {
      dispatch({
        type: 'SET_DYNAMIC_GRID_PARAMS',
        payload: {
          queryTagsKey,
          paginationNo: currentPage,
        },
      });
    }
  }, [dispatch, currentPage, queryTagsKey]);

  // Use  context queryTagIds if filtering, otherwise use fields.queryTagIds
  const finalQueryTagIds = ctxDynamicGridParam?.queryTagIds || queryTagIds || fields.queryTagIds;

  const { data, isLoading, isError } = useDynamicPages(
    finalQueryTagIds,
    1, // Always fetch from page 1
    1000, // Fetch large limit to get all data
    assets,
    entries,
    router.locale,
    0, // No offset - start from beginning
    'all',
    CtfContentQueryOrder.CREATE_DATE === fields.contentQueryOrder
      ? ContentQueryOrder.CREATE_DATE
      : ContentQueryOrder.PUBLISH_DATE
  );

  const [pageCount, setPageCount] = useState(
    Math.ceil(totalPages / fields.queryTagsLimit)
  );

  const getInjectedCards = (injectionCards, cards, pageNumber) => {
    // Don't inject into the middle - this breaks pinned content ordering
    // Instead, inject after pinned content but before non-pinned content
    const pinnedCards = cards.filter(card => card.pinnedContent === true);
    const nonPinnedCards = cards.filter(card => card.pinnedContent !== true);

    return [
      ...pinnedCards,
      injectionCards[pageNumber - 1],
      ...nonPinnedCards,
    ];
  };

  // function to turn pages into cards
  const pagesIntoCards = useCallback(
    (pages) => {
      const cards = pages?.map((i) => ({
        id: i.sys.id,
        primaryCTA: i.fields.slug,
        tags: i.fields.tags,
        title: i.fields.title,
        text: i.fields.previewText ?? null,
        cloudinaryAssets: i.fields.cloudinaryPreviewAssets ?? null,
        dynamicAsset: i.fields.previewAsset ?? null,
        pinnedContent: i.fields.pinnedContent ?? false,
        createdAt: i.sys.createdAt,
        publishedAt: i.sys.publishedAt || i.sys.createdAt,
      })) || [];
      return cards;
    },
    []
  );

  // load data from SSG into the initial state
  const [newFields, setNewFields] = useState(() => {
    // Sort SSG pages data to match API sorting
    const sortedPages = pages?.sort((a, b) => {
      // Handle missing pinnedContent field - treat as false if not present
      const aPinned = a.fields?.pinnedContent === true;
      const bPinned = b.fields?.pinnedContent === true;

      // If pinned status is different, sort pinned items first
      if (aPinned !== bPinned) {
        return bPinned - aPinned;
      }

      // For items with same pinned status, maintain original order
      return 0;
    });

    return {
      ...fields,
      cards: fields.cards
        ? getInjectedCards(
            fields.cards,
            pagesIntoCards(sortedPages),
            getPageNumber(router.asPath) || 1
          )
        : pagesIntoCards(sortedPages),
    };
  });

  //render data (CSR)
  useEffect(() => {
    if (!data) {
      return;
    }

    // Once API data is available, use it instead of SSG data
    setNewAssets([...assets, ...data.filteredNewAssets]);
    setNewEntries([...entries, ...data.filteredNewEntries]);
    const pageNumber = ctxDynamicGridParam?.paginationNo || 1;
    const paginationLimit = ctxDynamicGridParam?.paginationLimit || fields.queryTagsLimit;

    // Get all cards and sort them properly
    let allCards = pagesIntoCards(data.resp?.data?.items);

    if (ctxDynamicGridParam?.isFiltering) {
      // When filtering, sort the filtered results to show pinned first
      const pinnedCards = allCards.filter(c => c.pinnedContent === true);
      const nonPinnedCards = allCards.filter(c => c.pinnedContent !== true);
      allCards = [...pinnedCards, ...nonPinnedCards];
    } else {
      // When NOT filtering, check if SSG has more pinned content than API - if so, merge them
      const ssgPinnedCount = pages?.filter(p => p.fields?.pinnedContent === true).length || 0;
      const apiPinnedCount = allCards.filter(c => c.pinnedContent === true).length;

      if (ssgPinnedCount > apiPinnedCount) {
        // Get SSG pinned cards
        const ssgPinnedPages = pages?.filter(p => p.fields?.pinnedContent === true) || [];
        const ssgPinnedCards = pagesIntoCards(ssgPinnedPages);

        // Get SSG pinned card IDs to avoid duplicates
        const ssgPinnedCardIds = new Set(ssgPinnedCards.map(card => card.id));

        // Filter out API cards that already exist in SSG pinned cards to prevent duplicates
        const apiCardsWithoutDuplicates = allCards.filter(card => !ssgPinnedCardIds.has(card.id));

        // Remove any remaining pinned cards from API data since we're using SSG pinned cards
        const apiNonPinnedCards = apiCardsWithoutDuplicates.filter(card => card.pinnedContent !== true);

        // Merge: SSG pinned cards first, then API non-pinned cards only
        allCards = [...ssgPinnedCards, ...apiNonPinnedCards];
      }
    }

    // Final safeguard: Remove any remaining duplicates
    const seenIds = new Set();
    allCards = allCards.filter(card => {
      if (seenIds.has(card.id)) {
        return false;
      }
      seenIds.add(card.id);
      return true;
    });

    // Calculate total pages based on all cards
    setPageCount(Math.ceil(allCards.length / paginationLimit));

    // Apply frontend pagination - slice the sorted cards for current page
    const startIndex = (pageNumber - 1) * paginationLimit;
    const endIndex = startIndex + paginationLimit;
    let cards = allCards.slice(startIndex, endIndex);

    // Handle injection cards
    let injectionCards = fields.cards;
    if (
      injectionCards &&
      injectionCards.length > 0 &&
      injectionCards.length <= pageNumber &&
      !ctxDynamicGridParam?.isFiltering
    ) {
      cards = getInjectedCards(injectionCards, cards, pageNumber);
    }

    setNewFields({
      ...fields,
      cards,
    });
  }, [
    assets,
    ctxDynamicGridParam?.isFiltering,
    ctxDynamicGridParam?.paginationLimit,
    ctxDynamicGridParam?.paginationNo,
    data,
    entries,
    fields,
    pagesIntoCards,
  ]);

  const handlePageChange = (e) => {
    const url = router.asPath;
    const urlPart = url.split('?');

    let urlPaths = urlPart[0].split('/');
    if (!isNaN(urlPaths[urlPaths.length - 1])) {
      urlPaths.pop();
    }

    analytics({
      window,
      document,
      detail: getPaginationDetail(e),
    });

    router.push(
      `${urlPaths.join('/')}/${e.selected + 1}${
        urlPart[1] ? '?' + urlPart[1] : ''
      }`,
      null,
      { shallow: true }
    );

    //if there is only 1 .aaco-content-grid, do animate scroll
    if(document.querySelectorAll('.aaco-content-grid').length === 1) {
      window.scrollTo({
        top: document.querySelector('.aaco-content-grid')?.offsetTop - 100 ?? 0,
        behavior: 'smooth',
      });
    }
  };

  let paginationLimit = fields.queryTagsLimit || PaginationDefaults.LIMIT;
  //set context's queryTagIds & paginationNo on first load (if has query params) / query params change
  useEffect(() => {
    const refetchData = async (url) => {
      const urlPart = url.split('?');
      const urlParams = new URLSearchParams(urlPart[[1]]);
      const filterParam = urlParams.get(queryTagsKey);
      const pageNumber = getPageNumber(url) || 1;
      let currentQueryTagIds = queryTagIds; // set queryTagIds default queryTagIds;

      if (filterParam && filterParam.length > 0) {
        // get tag ids
        const tagIdsResp = await getTagIdsBySlug(filterParam);
        const tagIdRespIds =
          tagIdsResp?.data?.items?.map((j) => j.sys.id) ?? [];
        const hasFilterApplied = tagIdRespIds?.length > 0
        // if has filter use filter tag, else , use default tag
        currentQueryTagIds = hasFilterApplied ? tagIdRespIds : currentQueryTagIds;
      }

      let offset = pageNumber > 1 ? (pageNumber - 1) * paginationLimit : 0;
      let limit = paginationLimit;
      // If we're injecting cards then calculate offset manually
      if (fields.cards && (!filterParam || !filterParam.length)) {
        limit--;
        offset =
          pageNumber > fields.cards.length
            ? fields.cards.length * (paginationLimit - 1) +
              (pageNumber - fields.cards.length - 1) * paginationLimit
            : (pageNumber - 1) * (paginationLimit - 1);
      }

      //set context's params data
      dispatch({
        type: 'SET_DYNAMIC_GRID_PARAMS',
        payload: {
          queryTagsKey,
          ...(queryTagIds && { queryTagIds: currentQueryTagIds }),
          ...(pageNumber && { paginationNo: pageNumber }),
          paginationLimit: limit,
          isFiltering: filterParam && filterParam.length > 0,
          offset: offset + (fields.queryTagsOffset ?? 0),
        },
      });
    };

    // refetch on first load (if has query params)
    if(router.asPath.includes('?')) {
      refetchData(router.asPath)
    }

    router.events.on('routeChangeStart', refetchData);
    return () => {
      router.events.off('routeChangeStart', refetchData);
    };
  }, [
    dispatch,
    fields.cards,
    fields.queryTagsLimit,
    queryTagIds,
    queryTagsKey,
    router.events,
  ]);

  if (!newFields.cards) return null;
  else if (isError)
    return (
      <div className="container">
        <Text tag="body">Something went wrong...</Text>
      </div>
    );

  return (
    <>
      <ContentGrid
        assets={newAssets}
        fields={newFields}
        entries={newEntries}
        className={`${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
        columnLayout={columnLayout}
        isFromTwoColumnContent={isFromTwoColumnContent}
        isFromContentColumn={isFromContentColumn}
        hasBackground={hasBackground}
      />
      {pageCount > 1 && paginationLimit > 6 && (
        <div className="flex justify-center items-center mt-20 mb-20">
          <ReactPaginate
            forcePage={(ctxDynamicGridParam?.paginationNo ?? 1) - 1}
            pageCount={pageCount > 1 ? pageCount : 0}
            pageRangeDisplayed={5}
            nextLabel=""
            previousLabel=""
            renderOnZeroPageCount={null}
            className="aaco-pagination"
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );
}

DynamicGrid.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

DynamicGrid.defaultProps = {
  assets: [],
  entries: [],
  fields: null,
};

export default DynamicGrid;
